import React, { useState, createContext, useContext, useMemo} from 'react';
import { useAccountContext } from './AccountContext';
import { useEventsContext } from './EventsContext';
import { useSocialContext } from './SocialContext';
import { useInboxContext } from './InboxContext';
import { useNotificationContext } from './NotificationContext';
import { useCacheContext } from './CacheContext';

// Import types for strong typing
import { AccountContextValue } from './AccountContext';
import { EventsContextValue } from './EventsContext';
import { SocialContextValue } from './SocialContext';
import { InboxContextValue } from './InboxContext';
import { NotificationContextValue } from './NotificationContext';
import { CacheContextValue } from './CacheContext';


interface HeaderContextValue {
    selectedId: number | undefined;
    setSelectedId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export interface AppContextType {
    accountCTX: AccountContextValue;
    eventsCTX: EventsContextValue;
    socialCTX: SocialContextValue;
    inboxCTX: InboxContextValue;
    notificationCTX: NotificationContextValue;
    headerCTX: HeaderContextValue;
    cacheCTX: CacheContextValue;
}

type ContextProps = {
    children:React.ReactNode;
}

const AppContext = createContext<AppContextType | null>(null);

type ContextProviderProps = {
    children: React.ReactNode;
}

const ContextProvider = ({ children }: ContextProviderProps) => {
    // Call each custom hook to get its state and methods
    const accountContextValue = useAccountContext();
    const { signedIn } = accountContextValue;
    const eventsContextValue = useEventsContext(signedIn);
    const socialContextValue = useSocialContext(signedIn);
    const inboxContextValue = useInboxContext(signedIn);
    const notificationContextValue = useNotificationContext();
    const cacheContextValue = useCacheContext();

    const [selectedId, setSelectedId] = useState<number | undefined>(0);

    // Manage the simple Payment state directly within the main provider

    // The value prop now includes the payment context
    const providerValue: AppContextType = useMemo(() => ({
        accountCTX: accountContextValue,
        eventsCTX: eventsContextValue,
        socialCTX: socialContextValue,
        inboxCTX: inboxContextValue,
        notificationCTX: notificationContextValue,
        headerCTX: { selectedId, setSelectedId },
        cacheCTX: cacheContextValue, 
    }), [
        accountContextValue, 
        eventsContextValue, 
        socialContextValue, 
        inboxContextValue, 
        notificationContextValue, 
        selectedId, 
        cacheContextValue
    ]);

    return (
        <AppContext.Provider value={providerValue}>
            {children}
        </AppContext.Provider>
    );
};

export default ContextProvider;


// --- Custom hook to easily consume the context ---
export const useContextState = () => {
    const context = useContext(AppContext);
    
    if (!context) {
        throw new Error("useContextState must be used within a ContextProvider");
    }

    return context;
};